import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Chip,
  Stack,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

export function formatScore(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export function scorePercent(score, max) {
  const s = Number(score);
  const m = Number(max);
  if (!Number.isFinite(s) || !Number.isFinite(m) || m <= 0) return 0;
  return Math.max(0, Math.min(100, (s / m) * 100));
}

export function JsonPreview({ value, maxHeight = 280 }) {
  const text =
    value === undefined || value === null
      ? '—'
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
  return (
    <Paper variant="outlined" sx={{ p: 1, maxHeight, overflow: 'auto', bgcolor: 'action.hover' }}>
      <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
        {text}
      </Typography>
    </Paper>
  );
}

export function ExpandBlock({ title, subtitle, children, defaultOpen = false, rightSlot }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" component="div">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {rightSlot ? <Box onClick={(e) => e.stopPropagation()}>{rightSlot}</Box> : null}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

export function ScoreBadge({ score, maxScore }) {
  const pct = scorePercent(score, maxScore);
  const color = pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error';
  return (
    <Chip
      size="small"
      color={color}
      variant="outlined"
      label={`${formatScore(score)} / ${formatScore(maxScore)}`}
      sx={{ fontWeight: 600 }}
    />
  );
}

export function ScoreBar({ score, maxScore }) {
  const pct = scorePercent(score, maxScore);
  const color = pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ flex: 1, height: 8, borderRadius: 1 }}
      />
      <Typography
        variant="caption"
        sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 64, textAlign: 'right' }}
      >
        {formatScore(score)} / {formatScore(maxScore)}
      </Typography>
    </Box>
  );
}

export function FlagsRow({ flags, variant = 'warning' }) {
  const list = Array.isArray(flags) ? flags.filter(Boolean) : [];
  if (list.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        None
      </Typography>
    );
  }
  const color = variant === 'critical' ? 'error' : 'warning';
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {list.map((code) => (
        <Chip key={`${variant}-${code}`} label={code} size="small" color={color} variant="outlined" />
      ))}
    </Stack>
  );
}

export function RemarksList({ remarks }) {
  const list = Array.isArray(remarks)
    ? remarks.filter((r) => typeof r === 'string' && r.trim())
    : [];
  if (list.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No remarks.
      </Typography>
    );
  }
  return (
    <List dense disablePadding sx={{ pl: 1 }}>
      {list.map((r, idx) => (
        <ListItem
          key={`remark-${idx}`}
          sx={{ py: 0.25, display: 'list-item', listStyleType: 'disc', ml: 2 }}
        >
          <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={r} />
        </ListItem>
      ))}
    </List>
  );
}

export function TableFrame({ children, maxHeight = 360 }) {
  return (
    <Paper variant="outlined" sx={{ maxHeight, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        {children}
      </Table>
    </Paper>
  );
}

export function ScoreBreakdownTable({ breakdown }) {
  const b = breakdown && typeof breakdown === 'object' ? breakdown : {};
  const rows = [
    { key: 'git', label: 'Git', slice: b.git },
    { key: 'api', label: 'API', slice: b.api },
    { key: 'ui', label: 'UI', slice: b.ui },
  ].filter((r) => r.slice && typeof r.slice === 'object');

  const totalScore = rows.reduce((sum, r) => sum + (Number(r.slice?.score) || 0), 0);
  const totalMax =
    Number(b.maxTotal) || rows.reduce((sum, r) => sum + (Number(r.slice?.maxScore) || 0), 0);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No score breakdown available.
      </Typography>
    );
  }

  return (
    <TableFrame maxHeight={260}>
      <TableHead>
        <TableRow>
          <TableCell width={140}>Component</TableCell>
          <TableCell>Score</TableCell>
          <TableCell width={120} align="right">
            Points
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} hover>
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {row.label}
              </Typography>
            </TableCell>
            <TableCell>
              <ScoreBar score={row.slice?.score} maxScore={row.slice?.maxScore} />
            </TableCell>
            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatScore(row.slice?.score)} / {formatScore(row.slice?.maxScore)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ bgcolor: 'action.hover' }}>
          <TableCell>
            <Typography variant="body2" fontWeight={700}>
              Total
            </Typography>
          </TableCell>
          <TableCell>
            <ScoreBar score={totalScore} maxScore={totalMax} />
          </TableCell>
          <TableCell
            align="right"
            sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
          >
            {formatScore(totalScore)} / {formatScore(totalMax)}
          </TableCell>
        </TableRow>
      </TableBody>
    </TableFrame>
  );
}

export function GitEvaluationTable({ git }) {
  const rows = Array.isArray(git?.breakdown) ? git.breakdown : [];
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No git rules executed.
      </Typography>
    );
  }
  const isDetailedRows = rows.some((r) => r && (r.name || r.status));
  if (isDetailedRows) {
    return (
      <TableFrame maxHeight={420}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell width={120}>Status</TableCell>
            <TableCell width={180}>Score</TableCell>
            <TableCell>Message</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={`git-detailed-${idx}`} hover>
              <TableCell>{row.name || '—'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.status || '—'}
                  color={String(row.status || '').toUpperCase() === 'PASS' ? 'success' : 'error'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <ScoreBar score={row.score} maxScore={row.maxScore} />
              </TableCell>
              <TableCell>{row.message || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableFrame>
    );
  }
  return (
    <TableFrame maxHeight={420}>
      <TableHead>
        <TableRow>
          <TableCell width={200}>Rule</TableCell>
          <TableCell width={180}>Score</TableCell>
          <TableCell>Remarks</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={`${row.ruleId || idx}`} hover>
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {row.ruleId || '—'}
              </Typography>
            </TableCell>
            <TableCell>
              <ScoreBar score={row.score} maxScore={row.maxScore} />
            </TableCell>
            <TableCell>
              <RemarksList remarks={row.remarks} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TableFrame>
  );
}

export function ApiEvaluationTable({ api }) {
  const rawRows = Array.isArray(api?.breakdown) ? api.breakdown : [];
  const isDetailedRows = rawRows.some((r) => r && (r.name || r.endpoint || r.method));
  if (isDetailedRows) {
    return (
      <TableFrame maxHeight={480}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell width={100}>Method</TableCell>
            <TableCell>Endpoint</TableCell>
            <TableCell width={120}>Status</TableCell>
            <TableCell width={180}>Score</TableCell>
            <TableCell>Message</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rawRows.map((row, idx) => (
            <TableRow key={`api-detailed-${idx}`} hover>
              <TableCell>{row.name || '—'}</TableCell>
              <TableCell>{row.method || '—'}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>{row.endpoint || '—'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.status || '—'}
                  color={String(row.status || '').toUpperCase() === 'PASS' ? 'success' : 'error'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <ScoreBar score={row.score} maxScore={row.maxScore} />
              </TableCell>
              <TableCell>{row.message || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableFrame>
    );
  }
  // The backend now scopes API evaluation to the student's assigned question
  // only, so `breakdown` should contain exactly one row with `selected: true`.
  // For historical submissions (pre per-question refactor) where every
  // question was scored and `selected` is absent/false, we keep all rows so
  // the old result pages still render something meaningful.
  const selectedRows = rawRows.filter((r) => r && r.selected === true);
  const rows = selectedRows.length > 0 ? selectedRows : rawRows;
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No API rubric rows.
      </Typography>
    );
  }
  return (
    <TableFrame maxHeight={480}>
      <TableHead>
        <TableRow>
          <TableCell width={160}>Question</TableCell>
          <TableCell width={100}>Set</TableCell>
          <TableCell width={90} align="center">
            Selected
          </TableCell>
          <TableCell width={180}>Score</TableCell>
          <TableCell width={110} align="right">
            Passed / Total
          </TableCell>
          <TableCell>Remarks</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => {
          const questionLabel = row.questionCode || row.questionId || `Q${idx + 1}`;
          const passed = Number.isFinite(Number(row.passedTestCount))
            ? Number(row.passedTestCount)
            : null;
          const total = Number.isFinite(Number(row.totalTestCount))
            ? Number(row.totalTestCount)
            : null;
          const failed = Array.isArray(row.failedTestCases) ? row.failedTestCases : [];
          return (
            <React.Fragment key={`${row.questionId || idx}`}>
              <TableRow hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {questionLabel}
                  </Typography>
                  {row.questionId && row.questionId !== questionLabel ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {row.questionId}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  {row.setKey ? <Chip label={row.setKey} size="small" variant="outlined" /> : '—'}
                </TableCell>
                <TableCell align="center">
                  {row.selected ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <HighlightOffIcon color="disabled" fontSize="small" />
                  )}
                </TableCell>
                <TableCell>
                  <ScoreBar score={row.score} maxScore={row.maxScore} />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {passed !== null && total !== null ? `${passed} / ${total}` : '—'}
                </TableCell>
                <TableCell>
                  <RemarksList remarks={row.remarks} />
                </TableCell>
              </TableRow>
              {failed.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: 'action.hover', py: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="error">
                      Failed test cases ({failed.length})
                    </Typography>
                    <Table size="small" sx={{ mt: 0.5 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell width={180}>Name</TableCell>
                          <TableCell>Expected</TableCell>
                          <TableCell>Actual</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {failed.map((f, fi) => (
                          <TableRow key={`failed-${idx}-${fi}`}>
                            <TableCell>
                              <Typography variant="body2">{f.name || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                              >
                                {f.expected || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                              >
                                {f.actual || '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableCell>
                </TableRow>
              ) : null}
            </React.Fragment>
          );
        })}
      </TableBody>
    </TableFrame>
  );
}

export function UiEvaluationTable({ ui }) {
  const rawRows = Array.isArray(ui?.breakdown) ? ui.breakdown : [];
  const isDetailedRows = rawRows.some((r) => r && (r.name || r.status));
  if (isDetailedRows) {
    return (
      <TableFrame maxHeight={420}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell width={120}>Status</TableCell>
            <TableCell width={180}>Score</TableCell>
            <TableCell>Message</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rawRows.map((row, idx) => (
            <TableRow key={`ui-detailed-${idx}`} hover>
              <TableCell>{row.name || '—'}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.status || '—'}
                  color={String(row.status || '').toUpperCase() === 'PASS' ? 'success' : 'error'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <ScoreBar score={row.score} maxScore={row.maxScore} />
              </TableCell>
              <TableCell>{row.message || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableFrame>
    );
  }
  // Per-question UI rubric rows carry `selected: true` on the assigned
  // question only — scope the table to that row. Heuristic (`evaluateUiLight`)
  // breakdowns are section-based (no `selected` field on any row), so when no
  // row is explicitly selected we render everything as before.
  const selectedRows = rawRows.filter((r) => r && r.selected === true);
  const rows = selectedRows.length > 0 ? selectedRows : rawRows;
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No UI breakdown rows.
      </Typography>
    );
  }
  return (
    <TableFrame maxHeight={420}>
      <TableHead>
        <TableRow>
          <TableCell width={160}>Section</TableCell>
          <TableCell width={180}>Score</TableCell>
          <TableCell width={120} align="center">
            Confidence
          </TableCell>
          <TableCell>Remarks</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => {
          const id = row.id || row.ruleId || `section-${idx + 1}`;
          const label = id.charAt(0).toUpperCase() + id.slice(1);
          const confidence = Number(row.confidence);
          return (
            <TableRow key={id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {label}
                </Typography>
              </TableCell>
              <TableCell>
                <ScoreBar score={row.score} maxScore={row.maxScore} />
              </TableCell>
              <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : '—'}
              </TableCell>
              <TableCell>
                <RemarksList remarks={row.remarks} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </TableFrame>
  );
}

export function EvaluatorFlagsBlock({ flags }) {
  const list = Array.isArray(flags) ? flags : [];
  if (list.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Evaluator flags
      </Typography>
      <FlagsRow flags={list} variant="warning" />
    </Box>
  );
}

/**
 * Normalize a lab submission detail payload into the git/api/ui evaluator slices
 * the shared tables expect. Works for both faculty review (`detail.git.score` etc.)
 * and student detail (`detail.gitScore`, `detail.gitBreakdown`, `detail.evaluationDetails.*`) shapes.
 *
 * @param {Record<string, any> | null | undefined} detail
 */
export function normalizeEvaluatorSlices(detail) {
  const d = detail && typeof detail === 'object' ? detail : {};
  const ed = d.evaluationDetails && typeof d.evaluationDetails === 'object' ? d.evaluationDetails : {};

  const readSlice = (kind) => {
    const key = kind; // 'git' | 'api' | 'ui'
    const scoreKey = `${key}Score`;
    const breakdownKey = `${key}Breakdown`;
    const edSlice = ed[key] && typeof ed[key] === 'object' ? ed[key] : {};
    const existing = d[key] && typeof d[key] === 'object' ? d[key] : null;

    if (existing) {
      return {
        score: existing.score ?? existing[scoreKey] ?? null,
        breakdown: existing.breakdown ?? null,
        flags: Array.isArray(existing.flags) ? existing.flags : [],
        remarks: Array.isArray(existing.remarks) ? existing.remarks : [],
      };
    }

    return {
      score: d[scoreKey] != null ? Number(d[scoreKey]) : edSlice[`${key}Score`] ?? null,
      breakdown: d[breakdownKey] ?? edSlice.breakdown ?? null,
      flags: Array.isArray(edSlice.flags) ? edSlice.flags : [],
      remarks: Array.isArray(edSlice.remarks) ? edSlice.remarks : [],
    };
  };

  return {
    git: readSlice('git'),
    api: readSlice('api'),
    ui: readSlice('ui'),
  };
}
